'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { IconShoppingCart } from '@tabler/icons-react'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { reportSentryError } from 'lib/sentry'
import { addToCartSeo, CartItem, useCartStore } from 'modules/cart'
import {
  extractCustomerFromClientSession,
  extractTokenFromClientSession} from 'modules/customer'
import {
  avabilityProductCheck,
  getProductBySlug,
  getProperProductForm,
  ProductTypes,
  sortProductsByCategory
} from 'modules/products'
import { SeoListName } from 'shared/analytics'
import Button from 'shared/ui/Button'
import { ButtonSizes } from 'shared/ui/Button/Button.enums'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import LoadingOverlay from 'shared/ui/LoadingOverlay'
import { Modal, ModalDescription, ModalTitle } from 'shared/ui/Modal'
import { theme } from 'shared/ui/utils/designTokens'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'

import { filterReorderableItems } from '../utils/saleOrders'

import ProfileSaleOrdersRepeatProductCard, {
  ChngeQuantityRepeatCb,
  OnReplaceProduct
} from './ProfileSaleOrdersRepeatProductCard'

import styles from './RepeatSaleOrderButton.module.scss'

const getRepeatProductId = (productId: string) =>
  `repeat_order_product_${productId}`

interface RepeatSaleOrderButtonProps {
  items: CartItem[]
  id?: string
  className?: string
  triggerButtonSize?: ButtonSizes
}

const RepeatSaleOrderButton = ({
  items,
  id,
  className,
  triggerButtonSize = ButtonSizes.medium
}: RepeatSaleOrderButtonProps) => {
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const user = extractCustomerFromClientSession(session)

  const router = useRouter()

  const { addToCart, cart } = useCartStore()

  const [open, setOpen] = useState(false)
  const [isAddingToCartLoading, setIsAddingToCartLoading] = useState(false)
  const [isProductsLoading, setIsProductsLoading] = useState(false)
  const [products, setProducts] = useState<ProductTypes[]>([])
  const [productsQuantity, setProductsQuantity] = useState<
    Record<string, number>
  >({})
  const [currentCartItemsQuantity, setCurrentCartItemsQuantity] = useState<
    Record<string, number>
  >({})
  const repeatOrderModalTrigger = useRef<HTMLButtonElement | null>(null)

  // Filtered before the fetch below: marketing products are absent from the
  // catalogue, so fetching one by slug would reject the whole Promise.all.
  const reorderableItems = useMemo(() => filterReorderableItems(items), [items])

  useEffect(() => {
    const currentCartItemsQuantityCalculation: Record<string, number> =
      cart?.items?.reduce((acc, item) => {
        acc[item.product.id] = item.quantity

        return acc
      }, {}) || {}

    setCurrentCartItemsQuantity(currentCartItemsQuantityCalculation)
  }, [open, cart?.items])

  useEffect(() => {
    if (!open || !reorderableItems.length) {
      setProducts([])
      setProductsQuantity({})
      setIsProductsLoading(false)

      return
    }

    const getProducts = async () => {
      try {
        setIsProductsLoading(true)
        const productsQuantityCalculation: Record<string, number> = {}

        const mergedProducts = await Promise.all(
          reorderableItems.map(async item => {
            const res = await getProductBySlug({ slug: item.product.slug })
            const product = res.data.data

            productsQuantityCalculation[item.product.id] = item.orderedQuantity

            return {
              ...item.product,
              ...product
            }
          })
        )

        setProducts(mergedProducts)
        setProductsQuantity(productsQuantityCalculation)
      } catch (err) {
        console.error(err)
        toast.error(getApiErrors(err))
      } finally {
        setIsProductsLoading(false)
      }
    }

    void getProducts()

    return () => {
      setProducts([])
      setProductsQuantity({})
    }
  }, [open])

  const productsByCategory = useMemo(() => {
    const filteredProducts = products.filter(
      product => productsQuantity[product.id] > 0
    )

    const sortedProducts = sortProductsByCategory(filteredProducts)

    return sortedProducts
  }, [products, productsQuantity])

  const productsToAdd = useMemo(() => {
    return products.filter(product => productsQuantity[product.id] > 0)
  }, [products, productsQuantity])

  const invalidProducts = useMemo(() => {
    return productsToAdd.filter(product => {
      const { isAvailable, quantityLimit } = avabilityProductCheck(product)

      const currentCartItemQuantity = currentCartItemsQuantity[product.id] || 0

      return (
        !isAvailable ||
        quantityLimit === 0 ||
        quantityLimit < productsQuantity[product.id] + currentCartItemQuantity
      )
    })
  }, [productsToAdd, productsQuantity, cart?.items])

  const hasNothingToRepeat = !isProductsLoading && reorderableItems.length === 0

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handleStartRepeatOrder = () => {
    setIsProductsLoading(true)
    handleOpen()
  }

  const handleonChangeQuantity =
    (productId: string) => (v: number | ChngeQuantityRepeatCb) => {
      setProductsQuantity(prev => ({
        ...prev,
        [productId]: typeof v === 'function' ? v(prev[productId]) : v
      }))
    }
  const handleReplaceProduct = (replacePayload: OnReplaceProduct) => {
    const { oldProduct, newProduct } = replacePayload

    // In suggested Products we don't have information about categoriesTree
    //so we add nw Product to based product category
    const newProductTree =
      newProduct?.categoriesTree || oldProduct?.categoriesTree

    const newProductWithCategories = {
      ...newProduct,
      categoriesTree: newProductTree
    }

    setProductsQuantity(prev => ({
      ...prev,
      [oldProduct.id]: 0,
      [newProductWithCategories.id]: productsQuantity[oldProduct.id] || 1
    }))

    setProducts(prev => [...prev, newProductWithCategories])
  }

  const handleRepeatOrder = () => {
    if (productsToAdd.length === 0) {
      return
    }

    setIsAddingToCartLoading(true)
    const payload = productsToAdd.map(product => ({
      productId: product.id,
      quantity: productsQuantity[product.id]
    }))

    addToCart({ items: payload, givenCartId: cart?.id }, session)
      .then(() => {
        router.push('/koszyk')

        handleClose()
        toast.success('Dodano produkty do koszyka')

        setIsAddingToCartLoading(false)
        productsToAdd.forEach(product => {
          addToCartSeo({
            product,
            user,
            idxOnList: 0,
            button_place: 'Zamów ponownie',
            productQuantity: productsQuantity[product.id] || 1,
            listName: SeoListName.REPEAT_ORDER_PRODUCTS,
            cart,
            type: 'add'
          })
        })
      })
      .catch(err => {
        toast.error(getApiErrors(err))
        setIsAddingToCartLoading(false)

        reportSentryError(err, 'Repeat order button error')
      })
  }

  const handleScrollIntoIssueProduct = (productId: string) => {
    const productElement = document.getElementById(
      getRepeatProductId(productId)
    )

    if (productElement) {
      productElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }

  return (
    <div id={id} className={className}>
      <Button
        id='repeat_order_repeat_button'
        variant='neutralOutline'
        startIcon={<IconShoppingCart />}
        onClick={handleStartRepeatOrder}
        ref={repeatOrderModalTrigger}
        size={triggerButtonSize}
      >
        Zamów ponownie
      </Button>
      {isProductsLoading ? (
        <LoadingOverlay />
      ) : (
        <Modal
          id='repeat-order-modal'
          open={open}
          onClose={handleClose}
          elementToReturnFocus={repeatOrderModalTrigger}
        >
          <ModalTitle>Ponów zamówienie</ModalTitle>
          <ModalDescription>
            {hasNothingToRepeat
              ? 'To zamówienie zawierało wyłącznie produkty promocyjne, których nie dodajemy ponownie. Nie ma tu nic do zamówienia.'
              : 'Ponowienie zamówienia spowoduje dodanie poniższych produktów do Twojego koszyka.'}
          </ModalDescription>
          <div className={styles.listWrapper}>
            {productsByCategory?.map(category => {
              const categoryItems = category?.items || []

              return (
                <div key={category?.categoryId}>
                  <div className={styles.categoryTitle}>
                    <div>{category.categoryName}</div>
                    <div className={styles.productsCount}>
                      {getProperProductForm(categoryItems.length)}
                    </div>
                  </div>
                  <ul className={styles.productsList}>
                    {categoryItems.map(product => (
                      <ProfileSaleOrdersRepeatProductCard
                        key={product.id}
                        product={product}
                        onChangeQuantity={handleonChangeQuantity(product.id)}
                        quantity={productsQuantity[product.id] || 0}
                        token={token}
                        onReplaceProduct={handleReplaceProduct}
                        id={getRepeatProductId(product.id)}
                        currentCartItemQuantity={
                          currentCartItemsQuantity[product.id] || 0
                        }
                      />
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
          {invalidProducts.length > 0 && (
            <div className={styles.invalidProductsWrapper}>
              <h3 className={styles.invalidProductsTitle}>
                Poniższe produkty są niedostępne:
              </h3>
              {invalidProducts.map(product => {
                const { isAvailable, quantityLimit } =
                  avabilityProductCheck(product)

                return (
                  <div key={product.id} className={styles.invalidProduct}>
                    <div className={styles.invalidProductData}>
                      <div className={styles.invalidProductTitle}>
                        {product.nameDescriptionShort ||
                          product.name ||
                          product.nameShort}
                      </div>
                      <div className={styles.invalidProductDescription}>
                        {isAvailable
                          ? `Maksymalna dostępna ilość to: ${
                              quantityLimit -
                                currentCartItemsQuantity[product.id] || 0
                            }`
                          : `Produkt niedostępny - sprawdź zamienniki`}
                      </div>
                    </div>
                    <Button
                      variant='primary'
                      onClick={() => handleScrollIntoIssueProduct(product.id)}
                      size='small'
                    >
                      Pokaż
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
          <div className={styles.buttonsWrapper}>
            {!hasNothingToRepeat && (
              <Button
                id='repeat_order_add_products_button'
                fullWidth
                variant='primary'
                disabled={isAddingToCartLoading || invalidProducts.length > 0}
                startIcon={
                  isAddingToCartLoading ? (
                    <LoadingIndicator color={theme.colors.text.baseOnDark} />
                  ) : undefined
                }
                onClick={handleRepeatOrder}
              >
                Dodaj produkty
              </Button>
            )}
            <Button
              id='repeat_order_cancel_button'
              fullWidth
              variant='neutralOutline'
              onClick={handleClose}
            >
              {hasNothingToRepeat ? 'Zamknij' : 'Anuluj'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default RepeatSaleOrderButton
